import React from 'react';
import { APP_SWITCH_APPEARANCE } from "../../../../_Config/Plugins/AppSwitchAppearance";
import * as RadixToast from '@radix-ui/react-toast';
import { useTheme } from '../../../Hooks/Contexts/Theme/ThemeAppContext';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import { useReactNavigation } from '../../../Hooks/Hooks/useReactNavigation';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { Toaster as HotToaster, toast as hotToast } from 'react-hot-toast';
import { ToastContainer as BootstrapToastContainer } from 'react-bootstrap';
import { CONFIG_PLUGIN } from "../../../../_Config/Plugins/_Config.Plugin";
import { ToastContainer as ReactToastifyContainer, toast as toastifyToast } from 'react-toastify';

/*
    🔥 Lee las variables CSS EN EL MOMENTO en que se usan.

    Antes esto se resolvía una sola vez, al importar el archivo:

        const _TOASTER_ROOT = getComputedStyle(document.documentElement);

    El problema: los colores quedaban congelados con los del tema que hubiera
    al arrancar la app. Al pasar a modo oscuro, los íconos de hot-toast seguían
    pintados con el verde del modo claro.

    Ahora es una función: cada llamada relee el valor vigente del <html>.
*/
const TOASTER_VALUE = (name) => {
    if (typeof window === 'undefined') return '';
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
};

const SHOW_BTN_TEST = CONFIG_PLUGIN.TEMPLATES.HAS__SHOW_APP_TOASTER_NOTIFY_TEST;

const TOAST_META = {
    success: { icon: '✓', tag: 'OK', label: 'Éxito' },
    error: { icon: '✕', tag: 'ERR', label: 'Error' },
    warning: { icon: '▲', tag: 'WARN', label: 'Aviso' },
    info: { icon: 'ⓘ', tag: 'INFO', label: 'Info' },
    secondary: { icon: '◇', tag: 'NEUT', label: 'Nota' },
    loading: { icon: '◐', tag: 'PROC', label: 'Procesando' },
};

// ------------------------------------- DISPARADOR DE PRUEBA
const triggerTestToast = (type = 'success', scopeCls = 'ls-theme-public') => {
    const template = Number(APP_SWITCH_APPEARANCE.STYLES.ALERT);

    switch (template) {
        case 1:
        case 2:
            if (type === 'success') hotToast.success("¡Prueba exitosa en HotToast!", { className: `ls-hot-type-success ${scopeCls}` });
            if (type === 'error') hotToast.error("¡Error de prueba en HotToast!", { className: `ls-hot-type-error ${scopeCls}` });
            if (type === 'warning') hotToast("⚠️ Advertencia de prueba", { icon: '⚠️', className: `ls-hot-type-warning ${scopeCls}` });
            if (type === 'info') hotToast("ℹ️ Notificación informativa", { icon: 'ℹ️', className: `ls-hot-type-info ${scopeCls}` });
            if (type === 'secondary') hotToast("Mensaje secundario / neutro", { className: `ls-hot-type-secondary ${scopeCls}` });
            if (type === 'loading') hotToast.loading("Cargando proceso...", { className: `ls-hot-type-loading ${scopeCls}` });
            break;

        case 3:
            if (type === 'success') sonnerToast.success("¡Prueba exitosa en Sonner!");
            if (type === 'error') sonnerToast.error("¡Error de prueba en Sonner!");
            if (type === 'warning') sonnerToast.warning("Advertencia de prueba en Sonner");
            if (type === 'info') sonnerToast.info("Notificación informativa");
            if (type === 'secondary') sonnerToast("Mensaje secundario / neutro");
            if (type === 'loading') sonnerToast.loading("Cargando proceso...");
            break;

        case 4:
            if (type === 'success') toastifyToast.success("¡Prueba exitosa en Toastify!");
            if (type === 'error') toastifyToast.error("¡Error de prueba en Toastify!");
            if (type === 'warning') toastifyToast.warn("Advertencia de prueba en Toastify");
            if (type === 'info') toastifyToast.info("Notificación informativa");
            if (type === 'secondary') toastifyToast("Mensaje secundario / neutro");
            if (type === 'loading') toastifyToast.loading("Cargando proceso...");
            break;

        case 5: {
            const nsVariant = (type === 'secondary') ? 'default' : (type === 'loading') ? 'info' : type;
            const nsMsg = (type === 'loading') ? "Cargando proceso..." : (type === 'secondary') ? "Mensaje secundario / neutro"
                : `¡Prueba de tipo ${type.toUpperCase()} en Notistack!`;
            enqueueSnackbar(nsMsg, {
                variant: nsVariant,
                className: `ls-ns-type-${type}`,
            });
            break;
        }

        case 6:
            window.dispatchEvent(new CustomEvent('LS__RADIX_TOAST', {
                detail: { type, message: `¡Prueba ${type.toUpperCase()} en Radix Toast!` }
            }));
            break;

        default:
            hotToast.success("¡Prueba por defecto!");
            break;
    }
};

// ------------------------------------- 🎯 COMPONENTE BOTONES DE PRUEBA
const ToasterTestControl = ({ isPrivate }) => {
    if (!CONFIG_PLUGIN.CONTEXTS.HAS__NOTIFY_CONTEXT || !SHOW_BTN_TEST) return null;

    // El panel de test también adopta la estética del scope vía clases CSS
    const scopeClass = isPrivate ? 'ls-theme-private' : 'ls-theme-public';

    const types = ['success', 'error', 'warning', 'info', 'secondary', 'loading'];

    return (
        <div className={`LStudio__ls_toast_testbar ${scopeClass}`}>
            <span className="LStudio__ls_toast_testbar_label">
                {isPrivate ? '🔒' : '🌐'} [{APP_SWITCH_APPEARANCE.STYLES.ALERT}]
            </span>
            {
                types.map((t) => (
                    <button key={t} onClick={() => triggerTestToast(t, scopeClass)}
                        className={`LStudio__ls_toast_testbtn LStudio__ls_toast_testbtn_${t}`} >
                        {TOAST_META[t].icon} {t}
                    </button>
                ))
            }
        </div>
    );
};

// ------------------------------------- 🎯 PLANTILLA NIVEL 1 (react-hot-toast)
const ToasterLevel1 = ({ scopeClass, resolvedTheme }) => {

    /*
        🔥 resolvedTheme entra como prop SOLO para que este componente se
        vuelva a renderizar cuando cambie el tema. Al re-renderizar, los
        TOASTER_VALUE() de abajo releen las variables CSS ya actualizadas.
        Sin esto, los íconos quedan con los colores del tema anterior.
    */
    const iconSecondary = TOASTER_VALUE("--LS__app_hottoast_icon_secondary");

    return (
        <HotToaster key={resolvedTheme} gutter={8} containerClassName={scopeClass} toastOptions={{
            duration: 2000, className: `LStudio__app_content_ls_toast ${scopeClass}`,
            success: {
                className: `LStudio__app_content_ls_toast LStudio__app_content_ls_toast_success ${scopeClass}`,
                iconTheme: {
                    primary: TOASTER_VALUE("--LS__app_hottoast_success"),
                    secondary: iconSecondary,
                },
            }, error: {
                className: `LStudio__app_content_ls_toast LStudio__app_content_ls_toast_error ${scopeClass}`,
                iconTheme: {
                    primary: TOASTER_VALUE("--LS__app_hottoast_error"),
                    secondary: iconSecondary,
                },
            }, loading: {
                className: `LStudio__app_content_ls_toast LStudio__app_content_ls_toast_loading ${scopeClass}`,
                iconTheme: {
                    primary: TOASTER_VALUE("--LS__app_hottoast_info"),
                    secondary: iconSecondary,
                },
            },
        }} />
    );
};

// ------------------------------------- 🎯 PLANTILLA NIVEL 2 (react-hot-toast)
const ToasterLevel2 = ({ scopeClass }) => (
    <HotToaster position="top-right" containerClassName={scopeClass} toastOptions={{ className: `LStudio__toast_v2 ${scopeClass}` }} />
);

// ------------------------------------- 🎯 PLANTILLA NIVEL 3 (sonner)
const ToasterLevel3 = ({ scopeClass, isPrivate, isDark }) => (
    <SonnerToaster
        position="top-right"
        richColors={!isPrivate}
        closeButton
        theme={isDark ? 'dark' : 'light'}
        className={`LStudio__app_content_ls_sonner ${scopeClass}`}
    />
);

// ------------------------------------- 🎯 PLANTILLA NIVEL 4 (react-toastify)
const ToasterLevel4 = ({ scopeClass, isDark }) => (
    <ReactToastifyContainer position="top-right" autoClose={3000}
        theme={isDark ? 'dark' : 'light'}
        hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false}
        pauseOnFocusLoss draggable pauseOnHover className={`LStudio__app_content_ls_toastify ${scopeClass}`} />
);

// ------------------------------------- 🎯 PLANTILLA NIVEL 5 (notistack)
const ToasterLevel5 = ({ scopeClass }) => (
    <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000} className={`LStudio__app_content_ls_notistack ${scopeClass}`} />
);

// ------------------------------------- 🎯 MARCO DE ESQUINAS SVG (protagonista en público)
const CornerFrame = () => (
    <svg className="LStudio__ls_radix_frame" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path className="LStudio__ls_radix_corner" d="M2,16 L2,2 L16,2" />
        <path className="LStudio__ls_radix_corner" d="M84,2 L98,2 L98,16" />
        <path className="LStudio__ls_radix_corner" d="M98,84 L98,98 L84,98" />
        <path className="LStudio__ls_radix_corner" d="M16,98 L2,98 L2,84" />
    </svg>
);

// ------------------------------------- 🎯 PLANTILLA NIVEL 6 (radix-ui toast)
const ToasterLevel6 = ({ isPrivate, scopeClass }) => {
    const [useOpen, setOpen] = React.useState(false);
    const [useToastData, setToastData] = React.useState({ type: 'info', message: '' });

    React.useEffect(() => {
        const handleCustomToast = (event) => {
            setToastData(event.detail);
            setOpen(false);
            setTimeout(() => setOpen(true), 50);
        };

        window.addEventListener('LS__RADIX_TOAST', handleCustomToast);
        return () => window.removeEventListener('LS__RADIX_TOAST', handleCustomToast);
    }, []);

    const meta = TOAST_META[useToastData.type] || TOAST_META.info;
    const typeAttr = useToastData.type || 'info';

    return (
        <RadixToast.Provider swipeDirection="right">
            <RadixToast.Root open={useOpen} onOpenChange={setOpen}
                duration={3000} data-type={typeAttr}
                className={`LStudio__ls_radix_root ${scopeClass}`}  >
                <CornerFrame />
                <span className="LStudio__ls_radix_scanline" aria-hidden="true" />
                <span className="LStudio__ls_radix_accentbar" aria-hidden="true" />
                <div className="LStudio__ls_radix_body">
                    <span className="LStudio__ls_radix_icon" data-icon={typeAttr}>{meta.icon}</span>
                    <div className="LStudio__ls_radix_texts">
                        <span className="LStudio__ls_radix_tag">
                            {isPrivate ? meta.label : `[ ${meta.tag} ]`}
                        </span>
                        <RadixToast.Title className="LStudio__ls_radix_title">
                            {useToastData.message}
                        </RadixToast.Title>
                    </div>
                    <RadixToast.Close className="LStudio__ls_radix_close" aria-label="Cerrar">
                        ✕
                    </RadixToast.Close>
                </div>
                <span className="LStudio__ls_radix_progress" aria-hidden="true" />
            </RadixToast.Root>

            <RadixToast.Viewport className={`LStudio__app_content_ls_radix_viewport ${scopeClass}`} />
        </RadixToast.Provider>
    );
};

// ------------------------------------- 🎯 PLANTILLA NIVEL FALLBACK (react-bootstrap)
const DefaultBootstrapToaster = () => (
    <BootstrapToastContainer position="bottom-end" className="p-3">
        {/* Contenedor estándar de React Bootstrap */}
    </BootstrapToastContainer>
);

// -------------------------------------------------🔥🔥 APP TOASTER
export const AppToaster = () => {
    const navigation = useReactNavigation();
    const isPrivate = navigation?.isPrivateRoute() ?? false;
    const scopeClass = isPrivate ? 'ls-theme-private' : 'ls-theme-public';

    /*
        🔥 El tema activo. Sirve para dos cosas:
        1) Sonner y Toastify traen su propio tema interno y hay que avisarles.
        2) Que este componente se re-renderice al cambiar de tema, y así los
           colores que se leen por JS (nivel 1) se recalculen.
    */
    const { useDataTheme } = useTheme();
    const resolvedTheme = useDataTheme?.resolvedTheme ?? 'light';
    const isDark = resolvedTheme === 'dark';

    const renderToaster = () => {
        if (!CONFIG_PLUGIN.CONTEXTS.HAS__NOTIFY_CONTEXT) {
            return <DefaultBootstrapToaster />;
        }

        switch (Number(APP_SWITCH_APPEARANCE.STYLES.ALERT)) {

            case 1:
                return <ToasterLevel1 scopeClass={scopeClass} resolvedTheme={resolvedTheme} />;

            case 2:
                return <ToasterLevel2 scopeClass={scopeClass} />;

            case 3:
                return <ToasterLevel3 scopeClass={scopeClass} isPrivate={isPrivate} isDark={isDark} />;

            case 4:
                return <ToasterLevel4 scopeClass={scopeClass} isPrivate={isPrivate} isDark={isDark} />;

            case 5:
                return <ToasterLevel5 scopeClass={scopeClass} />;

            case 6:
                return <ToasterLevel6 isPrivate={isPrivate} scopeClass={scopeClass} />;

            default:
                return <ToasterLevel1 scopeClass={scopeClass} resolvedTheme={resolvedTheme} />;

        }
    };

    return (
        <>
            {renderToaster()}
            <ToasterTestControl isPrivate={isPrivate} />
        </>
    );
};